import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { Building } from '../types/building';
import { User } from '../types/user';
import { Photo } from '../types/photo';
import { addAuditLog } from './auditLog';

// Define the database schema
interface InstallTrackDB extends DBSchema {
  buildings: {
    key: string;
    value: Building;
    indexes: {
      'by-name': string;
    };
  };
  archivedBuildings: {
    key: string;
    value: Building;
    indexes: {
      'by-name': string;
    };
  };
  users: {
    key: string;
    value: User;
    indexes: {
      'by-email': string;
    };
  };
  photos: {
    key: string;
    value: Photo;
    indexes: {
      'by-apartment': string;
      'by-building': string;
    };
  };
  konzernIds: {
    key: string;
    value: any;
    indexes: {
      'by-value': string;
    };
  };
  auditLogs: {
    key: string;
    value: any;
    indexes: {
      'by-timestamp': Date;
    };
  };
  settings: {
    key: string;
    value: any;
  };
}

// Database version
const DB_VERSION = 1;
const DB_NAME = 'installtrack-pro';

// Initialize the database
export const initDB = async (): Promise<IDBPDatabase<InstallTrackDB>> => {
  try {
    const db = await openDB<InstallTrackDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, newVersion, transaction) {
        // Create object stores if they don't exist
        if (!db.objectStoreNames.contains('buildings')) {
          const buildingStore = db.createObjectStore('buildings', { keyPath: 'id' });
          buildingStore.createIndex('by-name', 'name');
        }

        if (!db.objectStoreNames.contains('archivedBuildings')) {
          const archivedBuildingStore = db.createObjectStore('archivedBuildings', { keyPath: 'id' });
          archivedBuildingStore.createIndex('by-name', 'name');
        }

        if (!db.objectStoreNames.contains('users')) {
          const userStore = db.createObjectStore('users', { keyPath: 'id' });
          userStore.createIndex('by-email', 'email', { unique: true });
        }

        if (!db.objectStoreNames.contains('photos')) {
          const photoStore = db.createObjectStore('photos', { keyPath: 'id' });
          photoStore.createIndex('by-apartment', 'apartmentId');
          photoStore.createIndex('by-building', 'buildingId');
        }

        if (!db.objectStoreNames.contains('konzernIds')) {
          const konzernIdStore = db.createObjectStore('konzernIds', { keyPath: 'id' });
          konzernIdStore.createIndex('by-value', 'value', { unique: true });
        }

        if (!db.objectStoreNames.contains('auditLogs')) {
          const auditLogStore = db.createObjectStore('auditLogs', { keyPath: 'id' });
          auditLogStore.createIndex('by-timestamp', 'timestamp');
        }

        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings', { keyPath: 'key' });
        }

        console.log(`Database upgraded from version ${oldVersion} to ${newVersion}`);
      }
    });

    console.log('IndexedDB initialized successfully');
    return db;
  } catch (error) {
    console.error('Error initializing IndexedDB:', error);
    throw error;
  }
};

// Get a database connection
let dbPromise: Promise<IDBPDatabase<InstallTrackDB>> | null = null;

export const getDB = (): Promise<IDBPDatabase<InstallTrackDB>> => {
  if (!dbPromise) {
    dbPromise = initDB();
  }
  return dbPromise;
};

// Building operations
export const getBuildings = async (): Promise<Building[]> => {
  try {
    const db = await getDB();
    return await db.getAll('buildings');
  } catch (error) {
    console.error('Error getting buildings from IndexedDB:', error);
    return [];
  }
};

export const getArchivedBuildings = async (): Promise<Building[]> => {
  try {
    const db = await getDB();
    return await db.getAll('archivedBuildings');
  } catch (error) {
    console.error('Error getting archived buildings from IndexedDB:', error);
    return [];
  }
};

export const getBuilding = async (id: string, archived = false): Promise<Building | null> => {
  try {
    const db = await getDB();
    const store = archived ? 'archivedBuildings' : 'buildings';
    return await db.get(store, id);
  } catch (error) {
    console.error('Error getting building from IndexedDB:', error);
    return null;
  }
};

export const saveBuilding = async (building: Building): Promise<void> => {
  try {
    const db = await getDB();
    await db.put('buildings', building);
  } catch (error) {
    console.error('Error saving building to IndexedDB:', error);
    throw error;
  }
};

export const saveArchivedBuilding = async (building: Building): Promise<void> => {
  try {
    const db = await getDB();
    await db.put('archivedBuildings', building);
  } catch (error) {
    console.error('Error saving archived building to IndexedDB:', error);
    throw error;
  }
};

export const deleteBuilding = async (id: string, archived = false): Promise<void> => {
  try {
    const db = await getDB();
    const store = archived ? 'archivedBuildings' : 'buildings';
    await db.delete(store, id);
  } catch (error) {
    console.error('Error deleting building from IndexedDB:', error);
    throw error;
  }
};

export const archiveBuilding = async (id: string): Promise<Building | null> => {
  try {
    const db = await getDB();
    const building = await db.get('buildings', id);
    
    if (!building) {
      throw new Error('Building not found');
    }
    
    const archivedBuilding = {
      ...building,
      archivedAt: new Date()
    };
    
    await db.delete('buildings', id);
    await db.put('archivedBuildings', archivedBuilding);
    
    return archivedBuilding;
  } catch (error) {
    console.error('Error archiving building in IndexedDB:', error);
    return null;
  }
};

export const unarchiveBuilding = async (id: string): Promise<Building | null> => {
  try {
    const db = await getDB();
    const building = await db.get('archivedBuildings', id);
    
    if (!building) {
      throw new Error('Archived building not found');
    }
    
    // Remove archival date
    const { archivedAt, ...buildingWithoutArchiveDate } = building;
    
    await db.delete('archivedBuildings', id);
    await db.put('buildings', buildingWithoutArchiveDate);
    
    return buildingWithoutArchiveDate;
  } catch (error) {
    console.error('Error unarchiving building in IndexedDB:', error);
    return null;
  }
};

// User operations
export const getUsers = async (): Promise<User[]> => {
  try {
    const db = await getDB();
    return await db.getAll('users');
  } catch (error) {
    console.error('Error getting users from IndexedDB:', error);
    return [];
  }
};

export const getUser = async (id: string): Promise<User | null> => {
  try {
    const db = await getDB();
    return await db.get('users', id);
  } catch (error) {
    console.error('Error getting user from IndexedDB:', error);
    return null;
  }
};

export const getUserByEmail = async (email: string): Promise<User | null> => {
  try {
    const db = await getDB();
    const index = db.transaction('users').store.index('by-email');
    return await index.get(email);
  } catch (error) {
    console.error('Error getting user by email from IndexedDB:', error);
    return null;
  }
};

export const saveUser = async (user: User): Promise<void> => {
  try {
    const db = await getDB();
    await db.put('users', user);
  } catch (error) {
    console.error('Error saving user to IndexedDB:', error);
    throw error;
  }
};

export const saveUsers = async (users: User[]): Promise<void> => {
  try {
    const db = await getDB();
    const tx = db.transaction('users', 'readwrite');
    await Promise.all(users.map(user => tx.store.put(user)));
    await tx.done;
  } catch (error) {
    console.error('Error saving users to IndexedDB:', error);
    throw error;
  }
};

export const deleteUser = async (id: string): Promise<void> => {
  try {
    const db = await getDB();
    await db.delete('users', id);
  } catch (error) {
    console.error('Error deleting user from IndexedDB:', error);
    throw error;
  }
};

// Photo operations
export const getPhotos = async (): Promise<Photo[]> => {
  try {
    const db = await getDB();
    return await db.getAll('photos');
  } catch (error) {
    console.error('Error getting photos from IndexedDB:', error);
    return [];
  }
};

export const getPhotosByApartment = async (apartmentId: string): Promise<Photo[]> => {
  try {
    const db = await getDB();
    const index = db.transaction('photos').store.index('by-apartment');
    return await index.getAll(apartmentId);
  } catch (error) {
    console.error('Error getting photos by apartment from IndexedDB:', error);
    return [];
  }
};

export const getPhotosByBuilding = async (buildingId: string): Promise<Photo[]> => {
  try {
    const db = await getDB();
    const index = db.transaction('photos').store.index('by-building');
    return await index.getAll(buildingId);
  } catch (error) {
    console.error('Error getting photos by building from IndexedDB:', error);
    return [];
  }
};

export const savePhoto = async (photo: Photo): Promise<void> => {
  try {
    const db = await getDB();
    await db.put('photos', photo);
  } catch (error) {
    console.error('Error saving photo to IndexedDB:', error);
    throw error;
  }
};

export const deletePhoto = async (id: string): Promise<void> => {
  try {
    const db = await getDB();
    await db.delete('photos', id);
  } catch (error) {
    console.error('Error deleting photo from IndexedDB:', error);
    throw error;
  }
};

// Settings operations
export const getSetting = async (key: string): Promise<any> => {
  try {
    const db = await getDB();
    return await db.get('settings', key);
  } catch (error) {
    console.error('Error getting setting from IndexedDB:', error);
    return null;
  }
};

export const saveSetting = async (key: string, value: any): Promise<void> => {
  try {
    const db = await getDB();
    await db.put('settings', { key, value });
  } catch (error) {
    console.error('Error saving setting to IndexedDB:', error);
    throw error;
  }
};

// Migration from localStorage to IndexedDB
export const migrateFromLocalStorage = async (
  progressCallback?: (progress: number, message: string) => void
): Promise<boolean> => {
  try {
    // Initialize progress
    if (progressCallback) {
      progressCallback(0, 'Starting migration...');
    }

    // Get all localStorage keys that start with 'installtrack_'
    const keys = Object.keys(localStorage).filter(key => key.startsWith('installtrack_'));
    
    if (keys.length === 0) {
      if (progressCallback) {
        progressCallback(100, 'No data to migrate');
      }
      return true;
    }

    // Initialize database
    const db = await getDB();

    // Migrate each type of data
    let progress = 0;
    const progressStep = 100 / keys.length;

    for (const key of keys) {
      const value = localStorage.getItem(key);
      if (!value) continue;

      try {
        if (key === 'installtrack_buildings') {
          if (progressCallback) {
            progressCallback(progress, 'Migrating buildings...');
          }
          
          const buildings = JSON.parse(value, (key, value) => {
            if (key === 'lastUpdated' && value) {
              return new Date(value);
            }
            return value;
          });
          
          const tx = db.transaction('buildings', 'readwrite');
          await Promise.all(buildings.map((building: Building) => tx.store.put(building)));
          await tx.done;
        } 
        else if (key === 'installtrack_archived_buildings') {
          if (progressCallback) {
            progressCallback(progress, 'Migrating archived buildings...');
          }
          
          const archivedBuildings = JSON.parse(value, (key, value) => {
            if (key === 'lastUpdated' || key === 'archivedAt') {
              return value ? new Date(value) : value;
            }
            return value;
          });
          
          const tx = db.transaction('archivedBuildings', 'readwrite');
          await Promise.all(archivedBuildings.map((building: Building) => tx.store.put(building)));
          await tx.done;
        } 
        else if (key === 'installtrack_users') {
          if (progressCallback) {
            progressCallback(progress, 'Migrating users...');
          }
          
          const users = JSON.parse(value, (key, value) => {
            if (key === 'lastLogin' && value) {
              return new Date(value);
            }
            return value;
          });
          
          const tx = db.transaction('users', 'readwrite');
          await Promise.all(users.map((user: User) => tx.store.put(user)));
          await tx.done;
        } 
        else if (key === 'installtrack_photos') {
          if (progressCallback) {
            progressCallback(progress, 'Migrating photos...');
          }
          
          const photos = JSON.parse(value, (key, value) => {
            if (key === 'createdAt' && value) {
              return new Date(value);
            }
            return value;
          });
          
          const tx = db.transaction('photos', 'readwrite');
          await Promise.all(photos.map((photo: Photo) => tx.store.put(photo)));
          await tx.done;
        } 
        else if (key === 'installtrack_konzern_ids') {
          if (progressCallback) {
            progressCallback(progress, 'Migrating Konzern IDs...');
          }
          
          const konzernIds = JSON.parse(value, (key, value) => {
            if ((key === 'assignedAt' || key === 'releasedAt') && value) {
              return new Date(value);
            }
            return value;
          });
          
          const tx = db.transaction('konzernIds', 'readwrite');
          await Promise.all(konzernIds.map((konzernId: any) => tx.store.put(konzernId)));
          await tx.done;
        } 
        else if (key === 'installtrack_audit_log') {
          if (progressCallback) {
            progressCallback(progress, 'Migrating audit logs...');
          }
          
          // Audit logs are encrypted, so we need to handle them differently
          // For now, we'll just store the encrypted value
          await saveSetting('audit_log_encrypted', value);
        } 
        else if (key === 'installtrack_current_user') {
          if (progressCallback) {
            progressCallback(progress, 'Migrating current user...');
          }
          
          const currentUser = JSON.parse(value, (key, value) => {
            if (key === 'lastLogin' && value) {
              return new Date(value);
            }
            return value;
          });
          
          await saveSetting('current_user', currentUser);
        } 
        else {
          // Other settings
          if (progressCallback) {
            progressCallback(progress, `Migrating setting: ${key}...`);
          }
          
          try {
            const settingValue = JSON.parse(value);
            await saveSetting(key.replace('installtrack_', ''), settingValue);
          } catch (e) {
            // If it's not JSON, store as is
            await saveSetting(key.replace('installtrack_', ''), value);
          }
        }
      } catch (error) {
        console.error(`Error migrating ${key}:`, error);
        // Continue with other keys
      }

      progress += progressStep;
      if (progressCallback) {
        progressCallback(Math.min(progress, 99), `Migrated ${key}`);
      }
    }

    // Mark migration as complete
    localStorage.setItem('indexeddb_migration_complete', 'true');
    await saveSetting('migration_complete', true);
    
    // Log the migration
    addAuditLog('update', 'system', 'Migrated data from localStorage to IndexedDB');

    if (progressCallback) {
      progressCallback(100, 'Migration complete');
    }

    return true;
  } catch (error) {
    console.error('Error migrating from localStorage to IndexedDB:', error);
    if (progressCallback) {
      progressCallback(0, `Migration failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
    return false;
  }
};

// Clean up localStorage after successful migration
export const cleanupLocalStorage = async (): Promise<void> => {
  try {
    const keys = Object.keys(localStorage).filter(key => key.startsWith('installtrack_'));
    
    for (const key of keys) {
      localStorage.removeItem(key);
    }
    
    // Keep the migration flag
    localStorage.setItem('indexeddb_migration_complete', 'true');
    
    // Log the cleanup
    addAuditLog('delete', 'system', 'Cleaned up localStorage after migration to IndexedDB');
  } catch (error) {
    console.error('Error cleaning up localStorage:', error);
    throw error;
  }
};

// Check if IndexedDB is supported
export const isIndexedDBSupported = (): boolean => {
  return 'indexedDB' in window;
};

// Check if migration is needed
export const isMigrationNeeded = (): boolean => {
  const migrationComplete = localStorage.getItem('indexeddb_migration_complete');
  return !migrationComplete && Object.keys(localStorage).some(key => key.startsWith('installtrack_'));
};

// Check if migration is complete
export const isMigrationComplete = (): boolean => {
  return localStorage.getItem('indexeddb_migration_complete') === 'true';
};